import React from 'react';
import {withMeta, Plugins, Atom} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  CHECKOUT_ERRORS,
  COMMERCE_ERROR_CATEGORY,
} from '@packages/systems/commerce/constants';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {
  getArrayOrElse,
  getInPojo,
  getNumber,
  getRecordOrElse,
  getString,
} from '@packages/utilities/data-utils';

const CommerceCheckoutErrorMsg = Atom.createRender(
  ({attributes, classes, generateAttributes, data}) => {
    const updatedClasses = classes.push('w-checkout-error-msg');

    const errorsByID =
      getArrayOrElse(getInPojo(data, ['texts']), [])
        .map((v: unknown) => getRecordOrElse(v, {}))
        .reduce<Record<string, unknown>>(
          (acc: Record<string, unknown>, text: Record<string, unknown>) => {
            const id = getString(getInPojo(text, ['id']));
            if (id) {
              acc[id] = {
                msg: getString(getInPojo(text, ['copy'])),
              };
            }
            return acc;
          },
          {}
        ) ?? {};
    const idx = getNumber(getInPojo(data, ['temp', 'index'])) || 0;
    const message = getString(getInPojo(data, ['texts', idx, 'copy']));

    const errorsAttr = {
      'data-w-info-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.INFO.id, 'msg'])
      ),
      'data-w-shipping-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.SHIPPING.id, 'msg'])
      ),
      'data-w-billing-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.BILLING.id, 'msg'])
      ),
      'data-w-payment-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.PAYMENT.id, 'msg'])
      ),
      'data-w-pricing-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.PRICING.id, 'msg'])
      ),
      'data-w-minimum-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.MINIMUM.id, 'msg'])
      ),
      'data-w-extras-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.EXTRAS.id, 'msg'])
      ),
      'data-w-product-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.PRODUCT.id, 'msg'])
      ),
      'data-w-invalid-discount-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.INVALID_DISCOUNT.id, 'msg'])
      ),
      'data-w-expired-discount-error': getString(
        getInPojo(errorsByID, [CHECKOUT_ERRORS.EXPIRED_DISCOUNT.id, 'msg'])
      ),
      'data-w-usage-reached-discount-error': getString(
        getInPojo(errorsByID, [
          CHECKOUT_ERRORS.USAGE_REACHED_DISCOUNT.id,
          'msg',
        ])
      ),
      'data-w-requirements-not-met-error': getString(
        getInPojo(errorsByID, [
          CHECKOUT_ERRORS.REQUIREMENTS_NOT_MET_DISCOUNT.id,
          'msg',
        ])
      ),
    } as const;

    const attributesWithLive = attributes.set('aria-live', 'polite');

    return (
      <div
        {...generateAttributes(attributesWithLive, updatedClasses)}
        {...errorsAttr}
      >
        {message}
      </div>
    );
  }
);

const config = Atom.create({
  displayName: 'Error Message',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Text')),
  },
  showEmpty: false,
  render: CommerceCheckoutErrorMsg,
  dataType: withMeta(
    {
      textStates: CHECKOUT_ERRORS,
      categories: COMMERCE_ERROR_CATEGORY,
      legacyStatePath: ['commerce'],
    },
    createIntegratedRootAliasInstance(
      qualify(Plugins.name('Commerce'), typeName('MultiTextEditAlias'))
    )
  ),
});

export default config;
